<template>
  <v-menu
    v-model="popoverMenu"
    :close-on-content-click="false"
    offset-y
    bottom
    content-class="popover-menu-content"
    :max-width="$vuetify.breakpoint.xsOnly ? '95vw' : '550px'"
  >
    <template v-slot:activator="{ on, attrs }">
      <span v-bind="attrs" v-on="on" class="popover-activator"></span>
    </template>
    <v-card class="rounded-xl elevation-24 border-0">
      <v-card-title class="py-3 px-4 bg-gray-100/50 border-b border-gray-100 d-flex align-center">
        <v-icon small color="primary" class="mr-2">mdi-email-edit-outline</v-icon>
        <span class="text-subtitle-2 font-bold text-gray-800">Communication Draft</span>
        <v-spacer></v-spacer>
        <v-btn icon x-small @click="closePopover" color="grey">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-4 pt-2">
        <div v-for="(field, index) in localFields" :key="index">
          <v-text-field
            v-model="field.value"
            :label="field.label"
            outlined
            dense
            hide-details
            class="mt-4 field-width"
            v-if="field.type === 'text'"
            :prepend-inner-icon="field.icon"
          >
          </v-text-field>
          <!-- Letter Attachment Selector (for Emails) -->
          <div v-if="field.type === 'editor' && templates.length" class="mt-4">
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="text-caption font-bold text-gray-600">Attach Letter Template</span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small v-bind="attrs" v-on="on" color="grey"
                    >mdi-help-circle-outline</v-icon
                  >
                </template>
                <span>The selected letter will be sent as a PDF attachment</span>
              </v-tooltip>
            </div>
            <v-select
              v-model="field.attachedLetter"
              :items="templates"
              item-text="name"
              label="Select Letter to Attach"
              dense
              outlined
              hide-details
              prepend-inner-icon="mdi-paperclip"
              class="rounded-lg bg-gray-50/50"
              return-object
              clearable
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="font-medium">{{ item.name }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-select>

            <!-- Attachment Chip -->
            <div v-if="field.attachedLetter" class="mt-2 d-flex align-center">
              <v-chip
                close
                color="blue lighten-5"
                text-color="blue darken-3"
                class="font-weight-bold"
                label
                @click:close="field.attachedLetter = null"
              >
                <v-icon left small>mdi-file-pdf-box</v-icon>
                {{ field.attachedLetter.name }}
              </v-chip>
            </div>
          </div>

          <VueEditor
            v-model="field.value"
            v-if="field.type === 'editor'"
            :editor-toolbar="customToolbar"
            :placeholder="field.label"
            class="mt-4 field-width"
          >
          </VueEditor>
          <div v-if="field.type === 'slider'" class="mt-4 field-width">
            <div class="text-caption mb-2">{{ field.label }}</div>
            <v-slider
              v-model="field.value"
              :min="field.min"
              :max="field.max"
              :step="field.step || 1"
              thumb-label="always"
              hide-details
            ></v-slider>
          </div>
        </div>

        <div class="d-flex justify-space-between mt-2">
          <v-btn small class="normal-case" outlined color="red" @click="closePopover">
            <v-icon left small>mdi-close</v-icon>
            Cancel
          </v-btn>
          <div>
            <!-- <v-btn small outlined @click="copyLinkToClipboard" class="mr-2">
              <v-icon left small>mdi-content-copy</v-icon>
              Copy Link
            </v-btn> -->
            <v-btn
              small
              class="normal-case"
              color="primary"
              @click="saveFormResponse"
              :loading="isLoading"
            >
              <v-icon left small>mdi-content-save</v-icon>
              {{ saveButtonText }}
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>
<script>
import { VueEditor } from 'vue2-editor'
import { APP_URL } from '@/utils/workforce/constants'

export default {
  components: {
    VueEditor
  },
  data() {
    return {
      popoverMenu: false,
      localFields: [],
      templates: [],
      customToolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ header: 1 }, { header: 2 }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ align: [] }],
        ['clean'],
        ['link', 'image', 'video']
      ]
    }
  },
  props: {
    fields: Array,
    saveButtonText: { type: String, default: 'Save' },
    categoryData: { type: Object, default: null }, // Add category data prop
    isLoading: { type: Boolean, default: false }
  },
  watch: {
    fields: {
      handler(newFields) {
        this.localFields = newFields.map((field) => {
          // If this is an editor field and we have category data, pre-fill with the category link
          if (field.type === 'editor' && this.categoryData && this.categoryData._id) {
            const orgData = this.$storage ? this.$storage.getUniversal('orgData') : null
            if (orgData && (orgData._id || orgData.id)) {
              const orgId = orgData._id || orgData.id
              const baseUrl = APP_URL[process.env.VUE_APP_ENV || 'dev']
              const link = `${baseUrl}/open/form/${orgId}/${this.categoryData._id}`
              const linkHtml = `<p><a href="${link}" target="_blank" style="color: #2563eb; font-weight: bold; text-decoration: underline;">[LINK]</a></p>`
              return {
                ...field,
                value: field.value || linkHtml,
                attachedLetter: field.attachedLetter || null
              }
            }
          }
          return {
            ...field,
            value: field.value || '',
            attachedLetter: field.attachedLetter || null
          }
        })
      },
      immediate: true
    }
  },
  methods: {
    open() {
      this.fetchTemplates()
      this.popoverMenu = true
    },
    closePopover() {
      this.popoverMenu = false
    },
    copyLinkToClipboard() {
      this.$emit('copyShareLink')
    },
    async saveFormResponse() {
      // Emit the updated localFields data
      this.$emit('savePopoverResponse', this.localFields)
    },
    fetchTemplates() {
      try {
        const stored = localStorage.getItem('finfi_letter_templates')
        let localLetters = stored ? JSON.parse(stored) : []

        // If no templates, provide dummy ones as fallback
        if (!localLetters.length) {
          localLetters = [
            {
              _id: 'dummy_1',
              name: 'Loan Reminder (Standard)',
              content:
                '<p>Dear Customer,</p><p>This is a friendly reminder that your EMI is due. Please make the payment to avoid late fees.</p>',
              type: 'LETTER'
            },
            {
              _id: 'dummy_2',
              name: 'Settlement Offer',
              content:
                '<p>Dear Customer,</p><p>We are pleased to offer you a special settlement plan for your outstanding loan. Contact us for details.</p>',
              type: 'LETTER'
            }
          ]
        }
        this.templates = localLetters
      } catch (e) {
        console.error('Failed to fetch templates', e)
        this.templates = []
      }
    }
  },
  mounted() {
    this.fetchTemplates()
  }
}
</script>
<style scoped>
.field-width {
  width: 100%;
}

.vue2-editor {
  min-height: 250px;
  width: 100%;
}

.popover-menu-content {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
  border-radius: 16px !important;
  overflow: hidden;
}

.popover-activator {
  display: inline-block;
}

::v-deep .ql-tooltip {
  left: 30px !important; /* Force nudge right for links at start of lines */
  margin-top: 5px;
}
</style>
